import { Suspense } from 'react';
import { Await } from 'react-router';
import EditorialCard from '@/components/carousel-editorial-card/editorial-card';
import EditorialCardSkeleton from '@/components/carousel-editorial-card/editorial-card-skeleton';
import type { EditorialCardVariantId } from '@/components/carousel-editorial-card/bg-and-values-map';
import CustomPagination from '@/components/custom-pagination';
import type { ContentFolderPageCard, ContentFolderSearchResult } from '@/lib/api/content-search.server';
import { cn } from '@/lib/utils';
import NoArticlesMessage from '@/components/content-folder-listing/no-articles-message';
import { useTranslation } from 'react-i18next';
import type { PageWithComponentData } from 'src/lib/page-designer/page-loader.server';
import { Region } from '../region';

type ContentFolderListingGridProps = {
    gridClassName: string;
    page?: PageWithComponentData;
    kind: 'folderListing' | 'newsListing';
    isListingLoading: boolean;
    loadingSkeletonCount: number;
    criticalSearchResult: ContentFolderSearchResult;
    nonCriticalSearchResult?: Promise<ContentFolderSearchResult>;
    shouldRenderNonCritical: boolean;
    nonCriticalCount: number;
    criticalCount: number;
    currentPage: number;
    limit: number;
    setPageNumber?: (page: number) => void;
    cardVariant?: EditorialCardVariantId;
};
function resolveNewsCardSubtitle(item: ContentFolderPageCard): string {
    return item.subtitleString?.trim() || item.storeLocation?.trim() || item.folderTitle?.trim() || '';
}

export default function ContentFolderListingGrid({
    gridClassName,
    page,
    kind,
    isListingLoading,
    loadingSkeletonCount,
    criticalSearchResult,
    nonCriticalSearchResult,
    shouldRenderNonCritical,
    nonCriticalCount,
    criticalCount,
    currentPage,
    limit,
    setPageNumber,
    cardVariant = 'standard',
}: ContentFolderListingGridProps) {
    const { t } = useTranslation('search');
    const isSquaredCardLayout = cardVariant === 'square';
    if (isListingLoading) {
        return (
            <div className="grid-global" aria-busy="true">
                <div className={cn(gridClassName, 'col-span-full lg:col-start-2 lg:col-span-10')}>
                    {Array.from({ length: loadingSkeletonCount }, (_, i) => (
                        <EditorialCardSkeleton
                            key={i}
                            variant={cardVariant}
                            backgroundColorClass="bg-neutral-white"
                            className="max-h-[450px]"
                        />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <>
            {kind === 'newsListing' && page && <Region page={page} regionId="newsHighlighted" />}

            <div className="grid-global flex flex-col">
                <div className={cn(gridClassName, 'col-span-full lg:col-start-2 lg:col-span-10')}>
                    {criticalSearchResult.hits.map((pageItem) => (
                        <EditorialCard
                            key={pageItem.id}
                            title={pageItem.title}
                            subtitle={resolveNewsCardSubtitle(pageItem)}
                            linkUrl={pageItem.path}
                            linkText={!isSquaredCardLayout ? t('cardCta.read') : ''}
                            imageSrc={pageItem.imageSrc ?? ''}
                            variant={cardVariant}
                            description={isSquaredCardLayout ? (pageItem.storeLocation ?? '') : ''}
                            backgroundColorClass={isSquaredCardLayout ? 'bg-sand-95' : 'bg-neutral-white'}
                            headingClassTitle={isSquaredCardLayout ? 'Body Copy L Bold' : 'Title M'}
                            imageAlt={pageItem.title}
                            shadowEnabled={!isSquaredCardLayout}
                            className="max-h-[450px]"
                        />
                    ))}
                    {nonCriticalSearchResult && shouldRenderNonCritical ? (
                        <Suspense
                            fallback={Array.from({ length: nonCriticalCount }, (_, i) => (
                                <EditorialCardSkeleton
                                    key={i}
                                    variant={cardVariant}
                                    backgroundColorClass="bg-neutral-white"
                                    className="max-h-[450px]"
                                />
                            ))}>
                            <Await resolve={nonCriticalSearchResult}>
                                {(resolved) => (
                                    <>
                                        {resolved.hits.map((pageItem) => (
                                            <EditorialCard
                                                key={pageItem.id}
                                                title={pageItem.title}
                                                subtitle={resolveNewsCardSubtitle(pageItem)}
                                                linkUrl={pageItem.path}
                                                linkText={!isSquaredCardLayout ? t('cardCta.read') : ''}
                                                imageSrc={pageItem.imageSrc ?? ''}
                                                variant={cardVariant}
                                                description={isSquaredCardLayout ? (pageItem.storeLocation ?? '') : ''}
                                                backgroundColorClass={
                                                    isSquaredCardLayout ? 'bg-sand-95' : 'bg-neutral-white'
                                                }
                                                headingClassTitle={isSquaredCardLayout ? 'Body Copy L Bold' : 'Title M'}
                                                imageAlt={pageItem.title}
                                                shadowEnabled={!isSquaredCardLayout}
                                                className="max-h-[450px]"
                                            />
                                        ))}
                                        <NoArticlesMessage
                                            criticalSize={criticalCount}
                                            nonCriticalSize={resolved.hits.length}
                                        />
                                    </>
                                )}
                            </Await>
                        </Suspense>
                    ) : nonCriticalSearchResult ? (
                        Array.from({ length: nonCriticalCount }, (_, i) => (
                            <EditorialCardSkeleton
                                key={i}
                                variant={cardVariant}
                                backgroundColorClass={isSquaredCardLayout ? 'bg-sand-95' : 'bg-neutral-white'}
                                className="max-h-[450px]"
                            />
                        ))
                    ) : (
                        <NoArticlesMessage criticalSize={criticalCount} nonCriticalSize={nonCriticalCount} />
                    )}
                </div>
                <div className="col-span-full pb-14 lg:pb-20">
                    <CustomPagination
                        limit={limit}
                        currentPage={currentPage}
                        total={criticalSearchResult.total}
                        setPageNumber={setPageNumber}
                    />
                </div>
            </div>
        </>
    );
}
